import xeroIcon from '~/assets/public/xero-icon.png';
import { Icon, InlineTooltip, Tooltip } from '~/components';
import { useApi, useConfirmation, useIntegrations, useSubscription, useWorkspace } from '~/contexts';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { colors, weights } from '~/styles';
import RemoveXeroConfirmation from './RemoveXeroConfirmation';
import { useFeatures } from '~/hooks';

const Item = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 10.25rem;
  height: 9.375rem;
  margin: 1.25rem 0.625rem;
  background-color: ${colors.white};
  box-shadow: 0 0.1875rem 1rem ${colors.grey10};
  border: solid 0.125rem ${colors.white};
  border-radius: 0.3125rem;
  overflow: hidden;
`;

const Description = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Image = styled.img`
  width: 2.625rem;
  height: 2.625rem;
`;

const Name = styled.p`
  margin-top: 0.75rem;
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  min-height: 1.25rem;

  max-width: 9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Actions = styled.div`
  display: flex;
  height: 2rem;
  background-color: ${colors.grey10};
`;

const ActionStyle = css`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 2rem;
  padding: 0;
  color: ${colors.black};
  font-size: 0.75rem;
  font-weight: ${weights.normal};
  background-color: ${colors.grey5};
  border-radius: 0;

  &:not(:first-child) {
    margin-left: 1px;
  }

  &:hover {
    color: ${colors.black};
    background-color: ${colors.grey10};
  }
`;

const ActionAnchor = styled.a`
  ${ActionStyle}

  &:disabled {
    box-shadow: none;
    opacity: 0.5;

    &:hover {
      cursor: not-allowed;
    }
  }
`;

const ActionButton = styled.button`
  ${ActionStyle}

  position: relative;

  &:disabled {
    box-shadow: none;
    opacity: 0.5;

    &:hover {
      cursor: not-allowed;
    }
  }
`;

const WarningTooltip = styled(Tooltip)`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
`;

export default function XeroIntegration({ integration, onChange }) {
  const history = useHistory();
  const { url } = useRouteMatch();
  const confirmation = useConfirmation();
  const { notify } = useSubscription();
  const integrations = useIntegrations();
  const features = useFeatures();
  const [xeroConnection, setXeroConnection] = useState({ data: null, isReady: false });
  const { workspace } = useWorkspace();
  const api = useApi();

  const fetchXeroConnection = useCallback(async () => {
    if (!integration.isConnected) return;
    const { data } = await api.www.workspaces(workspace.id).xero(integration.xeroConnectionId).getStatus();
    setXeroConnection({ data, isReady: true });
  }, [api, integration, workspace]);

  useEffect(() => {
    fetchXeroConnection();
  }, [fetchXeroConnection]);

  const handleRemove = () => {
    if (!xeroConnection.isReady) return;

    confirmation.prompt((resolve) => (
      <RemoveXeroConfirmation
        xeroConnectionId={integration.xeroConnectionId}
        xeroConnectionName={xeroConnection.data?.name}
        onClose={resolve}
        onRemove={() => {
          if (typeof onChange === 'function') {
            onChange();
          }
          notify(useSubscription.keys.integrations_changed);
          resolve(true);
        }}
      />
    ));
  };

  return (
    <Item>
      <Description>
        <Image src={xeroIcon} />
        <Name>
          {!integration.isConnected ? 'Xero' : xeroConnection.isReady ? (xeroConnection.data?.name ?? 'Xero') : 'Xero'}
        </Name>
        {xeroConnection.isReady && xeroConnection.data?.status !== 'ok' && (
          <WarningTooltip message={xeroConnection.data.message}>
            <Icon icon="warning" color={colors.warning} />
          </WarningTooltip>
        )}
      </Description>
      {integration.isConnected ? (
        <Actions>
          <ActionButton onClick={() => history.push(`${url}/xero/${integration.xeroConnectionId}`)}>
            Settings
          </ActionButton>
          <ActionButton onClick={handleRemove} disabled={!xeroConnection.isReady}>
            Remove
          </ActionButton>
        </Actions>
      ) : (
        <Actions>
          <ActionAnchor
            href="https://help.ruddr.io/hc/en-us/articles/17805281548567-Xero-Integration-Overview"
            target="_blank"
            rel="noopener noreferrer">
            Details
          </ActionAnchor>
          {integrations.qbo && !features.multipleAccountingIntegrations ? (
            <ActionButton disabled>
              <InlineTooltip message={<>Remove other accounting integrations to install Xero.</>} />
              Install
            </ActionButton>
          ) : (
            <ActionAnchor href={integration.authorizeUri}>Install</ActionAnchor>
          )}
        </Actions>
      )}
    </Item>
  );
}
